/**
 * The privacy policy, served at /privacy so it has a stable public URL for the Play listing
 * and the Data safety form.
 *
 * Everything here is a description of what the code actually does. If the data model changes —
 * a new table, a new third-party call — this page changes with it, or it becomes a lie.
 */

const CONTACT_EMAIL = 'podcastie@paddez.com';
const CONTROLLER = 'Paddez Ltd';
const LAST_UPDATED = '10 August 2026';

export function Privacy() {
  return (
    <div class="view">
      <header class="view__header">
        <h1>Privacy policy</h1>
        <p class="muted">Last updated {LAST_UPDATED}</p>
      </header>

      <section class="section">
        <p class="muted">
          Podcastie is a podcast player operated by {CONTROLLER}, which is the data controller for
          the information described here. It stores what it needs to keep your subscriptions and
          your place in an episode in sync across your devices, and nothing beyond that. There is
          no advertising, no analytics, no tracking, and no third-party tracking code of any kind
          in the app or on the site.
        </p>
      </section>

      <section class="section">
        <h2 class="section__title">What is stored about you</h2>

        <h3>Account</h3>
        <ul class="muted">
          <li>Your email address, used to sign you in and to send sign-in links.</li>
          <li>A display name, if you set one.</li>
          <li>The date the account was created.</li>
          <li>
            Your settings: default playback speed, skip-forward and skip-back amounts, theme, and
            whether new episodes are added to your queue automatically.
          </li>
          <li>
            If you use a passkey: its credential identifier and public key. A public key cannot be
            used to sign in as you — the private half never leaves your device.
          </li>
        </ul>

        <h3>Listening</h3>
        <ul class="muted">
          <li>The podcasts you subscribe to, and when you subscribed.</li>
          <li>
            Your position in each episode, its length, your playback speed, whether you finished
            it, and when you last listened.
          </li>
          <li>Your queue and any playlists you create.</li>
        </ul>

        <p class="muted">
          Episodes you download in the Android app for offline listening are recorded on that
          device only. Nothing about what you have downloaded is sent to Podcastie.
        </p>

        <h3>Sessions</h3>
        <p class="muted">
          Signing in sets one cookie, <code>pc_session</code>, holding a random identifier and
          nothing else. It is marked HttpOnly and Secure so it cannot be read by scripts, and it
          expires after 30 days. It exists to keep you signed in, and is not used for tracking.
        </p>
      </section>

      <section class="section">
        <h2 class="section__title">What is not stored</h2>
        <p class="muted">
          No analytics or advertising identifiers. No location, contacts, photos, microphone or
          camera access. No profile is built about you, nothing is sold or shared for advertising,
          and nothing you listen to is used to target you.
        </p>
      </section>

      <section class="section">
        <h2 class="section__title">Who else sees anything</h2>
        <ul class="muted">
          <li>
            <strong>Cloudflare</strong> hosts the service and stores its database. As with any web
            host, its servers process your requests and keep standard logs, which include IP
            addresses.
          </li>
          <li>
            <strong>Resend</strong> delivers sign-in emails and therefore receives your email
            address when one is sent.
          </li>
          <li>
            <strong>Podcast Index</strong> receives the words you type when searching for a show.
            It is not told who searched.
          </li>
          <li>
            <strong>Podcast publishers.</strong> Audio is streamed straight from the publisher's
            own server rather than through Podcastie, so that server sees your IP address and app
            version, exactly as it would in any other podcast player. This is deliberate:
            proxying audio would mean routing every episode you play through us. Transcripts and
            chapter files <em>are</em> fetched by our server, so those requests do not expose you.
          </li>
        </ul>
      </section>

      <section class="section">
        <h2 class="section__title">The Android app</h2>
        <p class="muted">
          The Android app shows this same site and adds Android Auto support. It asks for internet
          access and permission to run a foreground service, which is what lets audio keep playing
          when the screen is off — nothing else. Episode audio and artwork are cached on the device
          so playback survives a poor signal; clearing the app's storage removes that cache. When
          you listen in the car, your position is sent back so your phone picks up where you left
          off.
        </p>
      </section>

      <section class="section">
        <h2 class="section__title">Keeping and deleting your data</h2>
        <p class="muted">
          Your data is kept until you delete your account. Deleting it removes your sign-in
          details, passkeys, subscriptions, queue, playlists, download records and listening
          history immediately and permanently. There is no grace period and no backup copy to
          restore from, so treat it as final.
        </p>
        <p class="muted">
          You can do this at any time from <a href="/settings">Settings</a>, or at{' '}
          <a href="/delete-account">/delete-account</a> if you have already removed the app.
        </p>
        <p class="muted">
          Podcast feeds and episodes themselves stay in the shared library after you go. They are
          public information about publicly available shows and contain nothing personal to you.
        </p>
      </section>

      <section class="section">
        <h2 class="section__title">Your rights</h2>
        <p class="muted">
          If you are in the UK or the European Economic Area, you have the right to access a copy
          of your data, correct it, have it erased, take it elsewhere, and object to how it is
          used. Erasure is built in and described above; for anything else, write to{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and you will get an answer within
          30 days. You may also complain to your national data protection authority.
        </p>
      </section>

      <section class="section">
        <h2 class="section__title">Children</h2>
        <p class="muted">
          Podcastie is not aimed at children under 13, and no account is knowingly created for one.
          If you believe a child has created an account, write to the address above and it will be
          removed.
        </p>
      </section>

      <section class="section">
        <h2 class="section__title">Changes</h2>
        <p class="muted">
          If this policy changes in a way that affects what is collected or who receives it, the
          date at the top changes and the app will say so before the change takes effect.
        </p>
      </section>

      <section class="section">
        <h2 class="section__title">Contact</h2>
        <p class="muted">
          {CONTROLLER}, <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </section>
    </div>
  );
}
